import {
  EllipsedText,
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  getFileNameFromPath,
  isValidImage,
  isValidLatitude,
  isValidLongitude,
  isValidVideo,
  useLoading,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { StatefulMenu } from 'baseui/menu';
import { StatefulPopover } from 'baseui/popover';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { MEDIA_SAFE_GPT_ORIGIN } from '../../../constants/media';
import { useCamera } from '../../../hooks/useCamera';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { getS3FileUrlByKey } from '../../../services/appService';
import { getDownloadMediaUrlByKey } from '../../../services/evidenceService';
import { gptAlarmTypeNameState } from '../../../storage/gptStates';
import { capturingTimelineEventState, capturingTimelineMapEventState } from '../../../storage/streaming';
import { StreamingTimelineEvent } from '../../../types/streaming';
import { downloadBlob } from '../../../utils/files';

type Props = Readonly<{
  event: StreamingTimelineEvent;
  highlight: boolean;
}>;

function CapturingVideoEvidence({ event, highlight = false }: Props) {
  const { theme, css, classes } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { startLoading: startDownloading, stopLoading: stopDownloading, loading: downloading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const { zoomTo } = useCamera();
  const [source, setSource] = useState('');
  const setEvent = useSetRecoilState(capturingTimelineEventState);
  const setMapEvent = useSetRecoilState(capturingTimelineMapEventState);
  const alarmType = useRecoilValue(gptAlarmTypeNameState(event?.alarmType));

  const mediaWrapperStyles = css({
    cursor: 'pointer',
    width: '220px',
    height: '110px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.colors.backgroundInversePrimary,
    position: 'relative',
  });
  const mediaStyles = css({ height: '100%', maxWidth: '100%', objectFit: 'contain' });

  useEffect(() => {
    const loadMedia = async () => {
      if (!event.key) {
        stopLoading();
        return;
      }
      startLoading();
      const { url, error } = await getS3FileUrlByKey(event.key, event.mediaOrigin === MEDIA_SAFE_GPT_ORIGIN);
      if (!error && url) {
        setSource(url);
      }
      stopLoading();
    };

    loadMedia();
  }, [event.key, event.mediaOrigin]);

  const download = async () => {
    if (!event.key) return;
    startDownloading();
    const { file, error } = await getDownloadMediaUrlByKey(event.key, event.mediaOrigin);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(event.key));
    }
    stopDownloading();
  };

  const onItemSelect = async (item: any, close: () => void) => {
    if (item.id === 'info') {
      setEvent(event);
    } else if (item.id === 'download') {
      download();
    }
    close();
  };

  const onLocateEvent = () => {
    setMapEvent(event);
    if (isValidLatitude(event.latitude) && isValidLongitude(event.longitude)) {
      zoomTo({
        lat: event.latitude,
        lng: event.longitude,
      });
    }
  };

  return (
    <FlexColumn
      gap={theme.sizing.scale0}
      classNames={css({
        borderRadius: theme.borders.buttonBorderRadius,
        animation: highlight ? 'highlightBackground 1.5s ease-in-out infinite' : undefined,
        alignItems: 'center',
        paddingTop: theme.sizing.scale0,
      })}
    >
      <LabelSmall color={theme.colors.contentTertiary}>{formatDateFromTs(+event.created)}</LabelSmall>
      <div
        onClick={onLocateEvent}
        className={mediaWrapperStyles}
        id={event.key}
      >
        {loading && <Loading size="default" />}
        {!loading && !source && <EmptyState title="No disponible" />}
        {!loading && source && (
          <>
            {isValidVideo(source) && (
              <video
                className={mediaStyles}
                autoPlay={false}
                controls
                controlsList="nofullscreen nodownload"
              >
                <source
                  src={source}
                  type="video/mp4"
                />
              </video>
            )}
            {isValidImage(source) && (
              <img
                src={source}
                alt=""
                className={mediaStyles}
              />
            )}
            <StatefulPopover
              content={({ close }) => (
                <StatefulMenu
                  overrides={{
                    ListItem: {
                      style: {
                        fontSize: '12px',
                      },
                    },
                  }}
                  onItemSelect={({ item, event }) => {
                    event?.stopPropagation();
                    onItemSelect(item, close);
                  }}
                  items={[
                    {
                      id: 'info',
                      label: (
                        <FlexRow
                          classNames={classes.centeredStart}
                          gap={theme.sizing.scale400}
                        >
                          <MaterialIcon
                            name="info"
                            size={theme.sizing.scale650}
                          />
                          Información
                        </FlexRow>
                      ),
                    },
                    {
                      id: 'download',
                      label: (
                        <FlexRow
                          classNames={classes.centeredStart}
                          gap={theme.sizing.scale400}
                        >
                          <MaterialIcon
                            name="download"
                            size={theme.sizing.scale650}
                          />
                          Descargar
                        </FlexRow>
                      ),
                    },
                  ]}
                />
              )}
              placement="bottomRight"
              triggerType="click"
              ignoreBoundary
              popoverMargin={5}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: theme.sizing.scale100,
                  right: theme.sizing.scale100,
                }}
              >
                <Button
                  size="mini"
                  kind="secondary"
                  shape="circle"
                  isLoading={downloading}
                  overrides={{
                    BaseButton: {
                      style: {
                        width: theme.sizing.scale800,
                        height: theme.sizing.scale800,
                        opacity: 0.8,
                      },
                    },
                  }}
                >
                  <MaterialIcon
                    name="more_vert"
                    size={theme.sizing.scale600}
                  />
                </Button>
              </span>
            </StatefulPopover>
          </>
        )}
      </div>
      {alarmType && (
        <LabelSmall>
          <EllipsedText
            inline
            breakAll
          >
            {alarmType}
          </EllipsedText>
        </LabelSmall>
      )}
    </FlexColumn>
  );
}

export default CapturingVideoEvidence;
